import { useEffect } from "react";
import {
  IIIFResource,
  loadManifest,
  parseManifest,
  Manifest,
  Collection,
  LanguageMap,
} from "manifesto.js";
// @ts-ignore
import { IIIFResourceType, MediaType } from "@iiif/vocabulary";
import { isEmpty, isLoaded } from "react-redux-firebase";
import {
  ExhibitImports,
  ExhibitDBValue,
  ItemDBValue,
  AnnotationDBValue,
  AnnotationMap,
  AnnotationTuple,
  ItemMap,
  ItemTuple,
  Exhibit,
} from "./reducer";

export const isProduction = process.env.NODE_ENV === "production";
export const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
export const measurementId = process.env.REACT_APP_MEASUREMENT_ID;
export const demoExhibit = process.env.REACT_APP_DEMO_EXHIBIT;
export const site = process.env.REACT_APP_SITE;
export const siteName = process.env.REACT_APP_SITE_NAME;

export const getMediaType = (item: IIIFResource | string): MediaType => {
  if (typeof item !== "string") {
    let context: string | string[] = item.context;
    if (Array.isArray(item.context)) {
      context = item.context.find(
        (value: string) => value.toLowerCase().indexOf("context.json") !== -1
      ) as string;
    }
    context = (context as string).toLowerCase();

    if (context.indexOf("presentation/2") !== -1) {
      return MediaType.IIIF_PRESENTATION_2;
    }

    if (context.indexOf("presentation/3") !== -1) {
      return MediaType.IIIF_PRESENTATION_3;
    }
  }

  return MediaType.IIIF_PRESENTATION_3;
};

// can't import from tailwind.js unfortunately...
export const sm: number = 640;
export const md: number = 768;
export const lg: number = 1024;
export const xl: number = 1280;

const getManifestLabel = (manifest: Manifest): string | null => {
  const label: LanguageMap | null = manifest.getLabel();

  if (label) {
    return LanguageMap.getValue(label);
  }

  return "unlabelled";
};

export const importItem = async (item: string): Promise<ExhibitImports> => {
  return new Promise<any>((resolve, reject) => {
    let imports: ExhibitImports = [];

    loadManifest(item)
      .then((json) => {
        const iiifResource: IIIFResource | null = parseManifest(json);

        if (iiifResource) {
          const mediaType: MediaType = getMediaType(iiifResource);

          if (
            iiifResource.getIIIFResourceType() === IIIFResourceType.COLLECTION
          ) {
            // it's a collection, get all the child manifests
            const manifests: Manifest[] = (iiifResource as Collection).getManifests();
            imports = manifests.map((manifest: Manifest) => {
              return {
                url: manifest.id,
                label: getManifestLabel(manifest),
                format: mediaType,
              };
            });
          } else {
            imports.push({
              url: item,
              label: getManifestLabel(iiifResource as Manifest),
              format: mediaType,
            });
          }

          resolve(imports);
        } else {
          reject(new Error("Unable to load IIIF manifest"));
        }
      })
      .catch((err) => {
        reject(new Error("Unable to load IIIF manifest"));
      });
  });
};

// parses the exhibit, items, and annotations into a single convenience object
export const useExhibit = ({
  exhibit,
  items,
  annotations,
  cb,
}: {
  exhibit: ExhibitDBValue;
  items: ItemDBValue[];
  annotations: AnnotationDBValue[];
  cb: (exhibit: Exhibit | null) => void;
}) => {
  useEffect(() => {
    if (isLoaded(exhibit) && isLoaded(items) && isLoaded(annotations)) {
      if (isEmpty(exhibit)) {
        cb(null);
      } else {
        // parse annotations and items into maps

        const annotationsArray: AnnotationDBValue[] = [...annotations];

        const annotationsMap: AnnotationMap = new Map(
          annotationsArray
            .sort((a, b) => {
              return a.orderIndex - b.orderIndex;
            })
            .map(
              (anno: AnnotationDBValue) =>
                [anno.id, { ...anno }] as AnnotationTuple
            )
        );

        const itemsArray: ItemDBValue[] = [...items];
        const itemsMap: ItemMap = new Map(
          itemsArray.map(
            (item: ItemDBValue) => [item.id, { ...item }] as ItemTuple
          )
        );

        console.log(exhibit.duplicationEnabled);

        const returnVal: Exhibit = {
          ...exhibit,
          items: itemsMap,
          annotations: annotationsMap,
          presentationType: exhibit.presentationType
            ? exhibit.presentationType
            : "slides",
          duplicationEnabled: !!exhibit.duplicationEnabled,
        };

        cb(returnVal);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exhibit, items, annotations]);
};
